import { create } from "zustand";
import type { ArretReason, Ordre, OrdreStatus } from "types/ordre";
import { ordresApi } from "@services/api/ordres";
import { dossiersApi } from "@services/api/dossiers";
import { showNotification } from "context/notification.service";

export interface ArretJustification {
  tag_ids: string[];
  arret_reason: ArretReason;
}

interface PendingRequest {
  clientName?: string;
  tagIds: string[];
  resolve: (result: ArretJustification | null) => void;
}

interface ArretJustificationState {
  pending: PendingRequest | null;
  ask: (opts: { clientName?: string; tagIds: string[] }) => Promise<ArretJustification | null>;
  resolve: (result: ArretJustification | null) => void;
}

/** Une seule modale de motif, montée à la racine ; les écrans l'appellent par promesse. */
export const useArretJustificationStore = create<ArretJustificationState>()((set, get) => ({
  pending: null,
  ask: (opts) =>
    new Promise((resolve) => {
      get().pending?.resolve(null);
      set({ pending: { ...opts, resolve } });
    }),
  resolve: (result) => {
    const p = get().pending;
    set({ pending: null });
    p?.resolve(result);
  },
}));

/**
 * Ce qu'un passage au statut `newStatus` doit écrire EN PLUS du statut.
 * Arrêter un ordre exige un motif (un tag suffit) : la modale le demande et la
 * réponse est renvoyée ici. `{}` quand il n'y a rien à demander, `null` quand
 * l'utilisateur a renoncé — l'hôte ne change alors pas le statut.
 * `known` = l'ordre tel que l'écran le tient ; sans lui (menu du planning) on
 * relit l'ordre pour ne pas écraser ses tags.
 */
export async function statusChangeUpdates(
  ordreId: string,
  newStatus: OrdreStatus,
  known?: Partial<Ordre> | null,
): Promise<Partial<Ordre> | null> {
  if (newStatus === "reception") {
    return (await canReceive(ordreId, known)) ? {} : null;
  }
  if (newStatus !== "arrete") return {};
  if (known?.status === "arrete") return {};
  let tagIds = known?.tag_ids;
  let clientName = known?.name;
  if (!tagIds || !clientName) {
    try {
      const full = await ordresApi.get(ordreId);
      if (full.status === "arrete") return {};
      tagIds = tagIds ?? (full.tag_ids || []);
      clientName = clientName ?? full.name;
    } catch {
      tagIds = tagIds ?? [];
    }
  }
  const justification = await useArretJustificationStore.getState().ask({ clientName, tagIds });
  if (!justification) return null;
  return { tag_ids: justification.tag_ids, arret_reason: justification.arret_reason };
}

/** Statuts d'avant la réception : en sortir vers « réceptionné » exige un contact client
 *  (miroir de RECEPTION_CONTACT_GUARDED_FROM côté back). */
const RECEPTION_GUARDED_FROM: ReadonlySet<string> = new Set(["rdv", "a_preparer", "devis_initial", "commande", "pret"]);
export const RECEPTION_CONTACT_REQUIRED_MSG =
  "Impossible de réceptionner : renseignez l'e-mail ou le téléphone du client sur sa fiche avant de passer l'ordre en « Réceptionné ».";

const filled = (v?: string | null) => !!v && v.trim().length > 0;

/**
 * Réceptionner exige un e-mail OU un téléphone client (demande Héric 2026-09-28) :
 * devis et notifications en ont besoin. Faux = bloqué (notification affichée).
 * En cas de doute (lecture impossible), on laisse passer : le back tranche (422).
 */
async function canReceive(ordreId: string, known?: Partial<Ordre> | null): Promise<boolean> {
  let ordre = known;
  if (!ordre?.status || ordre.dossier_id === undefined) {
    try {
      ordre = await ordresApi.get(ordreId);
    } catch {
      return true;
    }
  }
  if (!ordre?.status || !RECEPTION_GUARDED_FROM.has(ordre.status)) return true;
  const o = ordre as Partial<Ordre> & { client_email?: string };
  if (filled(o.client_email) || filled(o.devis_signer_email)) return true;
  let ok = false;
  if (ordre.dossier_id) {
    try {
      const d = await dossiersApi.get(ordre.dossier_id);
      ok = filled(d.email) || filled(d.phone) || (d.contacts || []).some((c) => filled(c.email) || filled(c.phone));
    } catch {
      return true;
    }
  }
  if (!ok) showNotification({ type: "error", message: RECEPTION_CONTACT_REQUIRED_MSG });
  return ok;
}

/** Message d'une erreur API (le 422 « motif requis » notamment), sinon `fallback`. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
}
